import { Component, EventEmitter, Input, Output, ViewChild, OnInit, AfterViewInit } from '@angular/core';
import { AuthService } from 'src/app/auth/auth.service';
import { NgModel } from '@angular/forms';
import { Profile } from 'src/app/general-components/profile/profile';
import { EmptyINDISpecial, INDISpecial } from '../indi-special';
import { ReservedSpecialSku } from '../../reserved-special-sku';
import { EmptyStats, Stats } from '../../suppliers/supplier';

@Component({
  selector: 'app-new-indi-special',
  templateUrl: './new-indi-special.component.html',
  styleUrls: ['./new-indi-special.component.scss'],
})
export class NewINDISpecialComponent implements OnInit, AfterViewInit {
  @Input() stats: Stats = EmptyStats();
  @Input() reservedSku: ReservedSpecialSku = { id: 0, sku: '' };
  @Input() supplierNames: string[] = [];
  @Output() keepInputs = new EventEmitter<boolean>();
  @Output() createNewSpecial = new EventEmitter<INDISpecial>();

  @ViewChild('description') description!: NgModel;
  @ViewChild('class2') class2!: NgModel;
  @ViewChild('indiSupplierName') indiSupplierName!: NgModel;
  @ViewChild('supplierSku') supplierSku!: NgModel;
  @ViewChild('purchasePrice') purchasePrice!: NgModel;
  @ViewChild('supplierPrice') supplierPrice!: NgModel;
  @ViewChild('salesPrice') salesPrice!: NgModel;
  @ViewChild('orderUnit') orderUnit!: NgModel;

  readonly supplierSKUPattern: string = '^[a-zA-Z0-9 :+=#\\u00D8,.&\\-\\/\\(\\)"]*$';

  showForm = false;

  newSpecial: INDISpecial = EmptyINDISpecial();
  profile!: Profile;

  margin: number | undefined;
  searchTextSpecialCode: string = '';

  private viewInitialized = false;

  constructor(private authService: AuthService) {}

  ngOnInit(): void {
    this.newSpecial.sku = this.reservedSku.sku;
    this.newSpecial.id = this.reservedSku.id;
    this.profile = this.authService.getUserInfo();
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
  }

  retryFailedSpecial(failedSpecial: INDISpecial) {
    this.newSpecial = failedSpecial;
  }

  toggleNewSpecial() {
    this.showForm = !this.showForm;
  }

  updatePricing() {
    this.newSpecial.supplierPrice = this.newSpecial.purchasePrice;
    this.updateSalesPrice();

    if (this.margin != undefined && this.newSpecial.supplierPrice != 0 && this.newSpecial.salesPrice != 0) {
      this.updateMargin();
    }
  }

  updateMargin() {
    if (this.newSpecial.supplierPrice != 0 && this.newSpecial.salesPrice != 0) {
      const diff = this.newSpecial.salesPrice - this.newSpecial.supplierPrice;
      const margin = (diff / this.newSpecial.salesPrice) * 100;
      this.margin = Number(margin.toFixed(2));
    }
  }

  updateSalesPrice() {
    if (this.margin != undefined && this.newSpecial.supplierPrice != 0) {
      const formattedMargin = (100 - this.margin) / 100;
      const newSalesPrice = this.newSpecial.supplierPrice / formattedMargin;
      this.newSpecial.salesPrice = Number(newSalesPrice.toFixed(4));
    }
  }

  toFixed(value: number, type: string) {
    switch (type) {
      case 'purchasePrice':
        this.newSpecial.purchasePrice = Number(value.toFixed(4));
        this.updatePricing();
        break;
      case 'supplierPrice':
        this.newSpecial.supplierPrice = Number(value.toFixed(4));
        this.updateMargin();
        break;
      case 'salesPrice':
        this.newSpecial.salesPrice = Number(value.toFixed(4));
        this.updateMargin();
        break;
      default:
        break;
    }
  }

  isFormValid(): boolean {
    if (!this.viewInitialized) {
      return false;
    }
    return Boolean(
      this.description.valid &&
        this.class2.valid &&
        this.indiSupplierName.valid &&
        this.supplierSku.valid &&
        this.purchasePrice.valid &&
        this.supplierPrice.valid &&
        this.salesPrice.valid &&
        this.margin &&
        this.margin >= 0 &&
        this.margin <= 99.99 &&
        this.orderUnit.valid
    );
  }

  createSpecial(special: INDISpecial, keepInputs: boolean) {
    special.id = this.reservedSku.id;
    special.sku = this.reservedSku.sku;
    special.creationBy = this.profile.fullName;
    this.keepInputs.emit(keepInputs);
    this.createNewSpecial.emit(this.cleanUpSpecial(special));
  }

  clearSpecial() {
    this.newSpecial = EmptyINDISpecial();
    this.description.reset();
    this.class2.reset();
    this.indiSupplierName.reset();
    this.supplierSku.reset();
    this.purchasePrice.reset();
    this.supplierPrice.reset();
    this.salesPrice.reset();
    this.searchTextSpecialCode = '';
    this.margin = undefined;
  }

  clearUniqueFields() {
    this.description.reset();
  }

  cleanUpSpecial(special: INDISpecial): INDISpecial {
    special.description = special.description.trim();
    special.indiSupplierName = special.indiSupplierName?.trim();
    return special;
  }
}
